/**
 * Validates AI Calling prompts against master template rules.
 */

import { AI_CALL_TYPES, normalizeCallType } from '@/utils/ai-calling-prompt-defaults'
import {
  getRequiredPromptSections,
  getRequiredPromptTokens,
  getRuntimePromptVariables,
} from '@/utils/ai-calling-catalog'

export const REQUIRED_SECTIONS = [
  'ROLE',
  'STYLE',
  'JOB DETAILS',
  'QUESTION FLOW',
  'SILENT CAPTURE',
]

export const REQUIRED_VARIABLES_FRESH = [
  'agent_name',
  'company',
  'client',
  'role',
  'location',
  'industry',
  'candidate_name',
  'min_experience_years',
  'preferred_qualification',
]

export const REQUIRED_VARIABLES_DRIVE = [
  ...REQUIRED_VARIABLES_FRESH,
  'drive_name',
  'drive_dates',
  'drive_location',
]

/** Supplied at call time via call_infield — not required as {{token}} in templates. */
export const RUNTIME_PROMPT_VARIABLES = ['candidate_name']

/** Job/infield aliases that satisfy a required variable when the canonical key is absent. */
const PROVIDED_VALUE_ALIASES = {
  client: ['client_name'],
  company: ['company_name'],
  role: ['job_title', 'position_name'],
  preferred_qualification: ['qualification'],
  min_experience_years: ['min_exp'],
}

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

/** Extract unique {{variable}} names from template text. */
export function extractPromptVariables(text) {
  const found = new Set()
  const str = String(text || '')
  let match = TOKEN_RE.exec(str)
  while (match) {
    found.add(match[1])
    match = TOKEN_RE.exec(str)
  }
  TOKEN_RE.lastIndex = 0
  return [...found]
}

export function isFilledPromptValue(value) {
  if (value == null) return false
  return String(value).trim() !== ''
}

/** Keys from a job / call_infields map that already have a non-empty value. */
export function collectProvidedVariableKeys(values = {}) {
  if (!values || typeof values !== 'object' || Array.isArray(values)) return []
  return Object.keys(values).filter(key => isFilledPromptValue(values[key]))
}

function hasProvidedVariable(key, provided) {
  if (provided.has(key)) return true
  const aliases = PROVIDED_VALUE_ALIASES[key] || []
  return aliases.some(alias => provided.has(alias))
}

/** Check required section headings exist (case-insensitive). */
export function findMissingSections(systemPrompt) {
  const text = String(systemPrompt || '')
  const upper = text.toUpperCase()
  return getRequiredPromptSections(REQUIRED_SECTIONS).filter(section => {
    const needle = section.toUpperCase()
    return !upper.includes(needle)
  })
}

function requiredVariablesForCallType(callType) {
  return callType === AI_CALL_TYPES.DRIVE
    ? getRequiredPromptTokens(AI_CALL_TYPES.DRIVE, REQUIRED_VARIABLES_DRIVE)
    : getRequiredPromptTokens(AI_CALL_TYPES.FRESH, REQUIRED_VARIABLES_FRESH)
}

/**
 * Validate system + first message prompts.
 *
 * A required variable is satisfied if the template still has `{{key}}` OR the
 * job / call_infields map already has a filled value (Gemini often returns a
 * fully substituted opening with no leftover tokens).
 *
 * @param {{
 *   systemPrompt?: string,
 *   firstMessage?: string,
 *   callType?: string,
 *   providedVariables?: string[],
 *   providedValues?: Record<string, unknown>,
 * }} opts
 * @returns {{ valid: boolean, errors: string[], warnings: string[], missingVariables: string[] }}
 */
export function validateAiCallingPrompt(opts = {}) {
  const callType = normalizeCallType(opts.callType)
  const systemPrompt = String(opts.systemPrompt || '').trim()
  const firstMessage = String(opts.firstMessage || '').trim()
  const providedVariables = new Set([
    ...(opts.providedVariables || []).map(v => String(v).trim()).filter(Boolean),
    ...collectProvidedVariableKeys(opts.providedValues),
  ])
  const errors = []
  const warnings = []
  const missingVariables = []

  if (!systemPrompt) {
    errors.push('Script content is missing.')
  }
  if (!firstMessage) {
    errors.push('Opening message is missing.')
  }

  if (callType === AI_CALL_TYPES.OTHER) {
    const hasQuestion = /Q\d+/i.test(systemPrompt)
    if (!hasQuestion) {
      errors.push('Custom script should include at least one question (Q1, Q2, …).')
    }
    return {
      valid: errors.length === 0,
      errors,
      warnings,
      missingVariables,
    }
  }

  const requiredVars = requiredVariablesForCallType(callType)
  const combined = `${systemPrompt}\n${firstMessage}`
  const present = new Set(extractPromptVariables(combined))

  requiredVars.forEach(key => {
    if (getRuntimePromptVariables(RUNTIME_PROMPT_VARIABLES).includes(key)) return
    if (!present.has(key) && !hasProvidedVariable(key, providedVariables)) {
      missingVariables.push(key)
    }
  })

  if (missingVariables.length) {
    errors.push(`Missing variables: ${missingVariables.map(v => `{{${v}}}`).join(', ')}`)
  }

  const leftoverRequiredTokens = requiredVars.filter(key => (
    !getRuntimePromptVariables(RUNTIME_PROMPT_VARIABLES).includes(key) && present.has(key)
  ))
  const fullySubstitutedWithValues = leftoverRequiredTokens.length === 0
    && missingVariables.length === 0
    && !!systemPrompt
    && !!firstMessage

  // Master-template section headings apply to token scripts. A Gemini / fully
  // substituted opening with job values already filled is valid without them.
  if (!fullySubstitutedWithValues) {
    findMissingSections(systemPrompt).forEach(s => {
      errors.push(`Missing required section: ${s}`)
    })
  }

  // Warn if backend tokens appear in spoken sections (heuristic)
  if (/speak.*call_status/i.test(systemPrompt) === false
    && /call_status=/.test(systemPrompt)
    && !/never speak.*call_status/i.test(systemPrompt)) {
    warnings.push('Ensure call_status instructions are marked as silent only.')
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    missingVariables,
  }
}

export default validateAiCallingPrompt
